// This is the config file for all the popups or notifications.

export const popups: PopupsSetup = {
    // Game Info
    antWorkerInfo: {
        label: 'help on worker ants',
        type: 'dialog',
        highlight: 'antControlsWorker',
        text: '*Workers* will gather food but won\'t explore far from home.\n\nCost: {ants.worker.creationCalories} calories\n{ants.worker.turnCalories} calorie per turn',
    },
    antWarriorInfo: {
        label: 'help on warrior ants',
        type: 'dialog',
        highlight: 'antControlsWarrior',
        text: '*Warriors* will defend your workers from attack. They will attack any enemy in sight.\n\nCost: {ants.warrior.creationCalories} calories\n{ants.warrior.turnCalories} calorie per turn',
    },
    antExplorerInfo: {
        label: 'help on explorer ants',
        type: 'dialog',
        highlight: 'antControlsExplorer',
        text: '*Explorers* are unpredictable but will allow you to access new areas and sources of food.\n\nCost: {ants.explorer.creationCalories} calories\n{ants.explorer.turnCalories} calorie per turn',
    },
    colonyInfo: {
        label: 'help on calorie reserve',
        type: 'dialog',
        highlight: 'colonyControls',
        text: 'These are your Colony\'s *calorie reserves*.\n\nYou use calories to feed your existing Colony and can also use them to build new ants.',
    },
    portfolioAllocationInfo: {
        label: 'help on portfolio allocation',
        type: 'dialog',
        highlight: 'antControls',
        text: 'You haven\'t allocated all your calories to your portfolio.',
    },

    // Chapter 1
    intro: {
        type: 'dialog',
        highlight: 'map',
        title: 'Congratulations!',
        text: 'You are a young Queen just establishing your Colony. You have absolute power in your domain.\n\nYour *first goal* is just to survive.',
        image: 'ant/queen/ant-queen.png',
        button: 'Start Playing',
    },
    unlockAntWorker: {
        type: 'dialog',
        highlight: 'antControlsWorker',
        text: 'You can *create Workers* to feed and care for you.\n\nThe Worker will look for Food, but won\'t go too far from home.\n\nThis will be the start of your Colony.',
    },
    explainHealth: {
        type: 'dialog',
        highlight: 'colonyControls',
        title: 'Your Colony is hungry!',
        text: 'Now that you\'ve created your first Worker, your Colony is using calories.\n\nKeep watch on your calories.\n\nIf you run out of calories, *your ants will start starving.*',
    },
    foundFirstFood: {
        type: 'dialog',
        highlight: 'map',
        text: 'Great, your Worker is now gathering calories for your Colony.\n\n*Create more Workers* to gather Food faster.',
    },
    builtTooFast: {
        type: 'notification',
        icon: 'danger',
        title: 'Find Food first',
        text: 'Slow down!\n\nWait until your Workers *find Food* before you create too many ants.',
        image: 'food/food-bread.png',
    },
    wontCreateAnotherAntWorker: {
        type: 'notification',
        highlight: 'antControlsWorker',
        title: 'Create more Workers',
        text: 'One Ant is pretty lonely.\n\n*Create another Worker* to find and gather more Food faster.',
    },
    starvation: {
        type: 'dialog',
        icon: 'danger',
        highlight: 'colonyControls',
        title: 'Uh oh!',
        text: 'You\'re out of Food and now your Colony will start *starving*.',
    },

    // Chapter 2
    consumedFirstFood: {
        type: 'dialog',
        icon: 'danger',
        highlight: 'colonyControls',
        title: 'Uh oh!',
        text: 'Looks like your Food source has run out and you are losing calories fast.\n\nYou\'d better *find new Food fast*!',
    },
    reserveLimitUnlocked: {
        type: 'dialog',
        icon: 'danger',
        highlight: 'colonyControls',
        title: 'Uh oh!',
        text: 'You are losing calories fast - *set a Reserve Limit*.\n\nThis will set some calories aside and your ants won\'t use them even if they are starving.\n\nThis is helpful if you\'ve built too many ants and you need to downsize while keeping some calories safe for later.\n\nYou can spend reserve calories manually.',
    },
    wontControlMap: {
        type: 'notification', // Don't let this get distracting.
        highlight: 'map',
        title: 'Take control!',
        text: 'You can pan and zoom the map by dragging or scrolling, try it out!',
    },
    foundBigFood: {
        type: 'dialog',
        highlight: 'map',
        title: 'Jackpot!',
        text: 'You\'ve found a huge source of calories.\n\nThis will allow you to survive and thrive.\n\n*New goal: Collect 4000 calories to survive the winter.*',
        image: 'food/food-cake.png',
    },

    // Chapter 3
    spottedDangerAnts: {
        type: 'dialog',
        highlight: 'map',
        title: 'Look out!',
        text: 'Looks like there is a competing Colony nearby and they are eating your lunch!\n\n*You have competition!*',
        image: 'danger/antWorker/danger-antWorker.png',
    },
    firstAntWorkerAttacked: {
        type: 'dialog',
        highlight: 'map',
        title: 'Oh no!',
        text: 'The enemy isn\'t just competing, they are *attacking and eating your Workers!*',
    },
    unlockAntWarrior: {
        type: 'dialog',
        highlight: 'antControlsWarrior',
        title: 'Unlocked: Warriors',
        text: 'Defend yourself by *building Warriors*.\n\nWarriors will help defend your supply line, but will *not* gather Food.',
    },

    // Chapter 4
    unlockPortfolioTool: {
        type: 'dialog',
        highlight: 'antControls',
        text: 'There\'s a lot going on now and you can\'t micromanage. Instead of building individual ants, use the *Portfolio Management Tool*.\n\nAllocate a % of your resources to build Workers or Warriors automatically.\n\nYour *Reserve Limit* will stop you from spending too much.',
    },
    noFoodInRange: {
        type: 'dialog',
        highlight: 'antControlsExplorer',
        title: 'This does not look good!',
        text: 'There are no food sources nearby and your workers can\'t go any further.\n\nThe colony seems doomed, but explorers are wildcards. Sometimes they come up with novel solutions to hard problems.\n\n*Try building explorers*.',
    },
    retry: {
        type: 'dialog',
        title: 'Oh oh!',
        text: 'Your colony has starved!\n\nYou\'re out of calories and out of workers! Try again.\n\nYou will reset back a bit and get another chance.',
        image: 'ant/queen/ant-queen.png',
        button: 'RESET',
    },
    nudgePortfolioTool: {
        type: 'dialog',
        highlight: 'antControls',
        text: 'You\'re a Queen - don\'t micromanage.\n\nUse the *Portfolio Management Tool* to implement your strategy.\n\nAllocate a % of your resources to build Workers or Warriors.',
    },

    // Chapter 5
    focusDangerAnteater: {
        type: 'dialog',
        highlight: 'map',
        title: 'Disaster!',
        text: 'Looks like a hungry Anteater has wandered in and is eating everything in its path.\n\nIn addition to worrying about competition, you need to worry that the playing field itself is volatile and unpredictable.\n\nYour Colony is fragile. If you stay here, you might not survive.\n\n*New goal: Find a safe food source before disaster strikes again.*',
    },
    unlockAntExplorer: {
        type: 'dialog',
        highlight: 'antControlsExplorer',
        title: 'Unlocked: Explorers',
        text: 'Your Workers won\'t go far from home, but Explorers will. You can use them to *find new sources of food*.\n\n Explorers can be a bit unpredictable.\n\nThey will seek out new territory but can also mutate. They can be very helpful or utterly useless.',
    },
    firstAntExplorerDeath: {
        type: 'dialog',
        highlight: 'map',
        text: 'Because Explorers enter new territory, they can get into trouble fast.\n\n*Build more Explorers* if you want to reveal more of the map.',
    },
    createdGoodAntExplorer: {
        type: 'dialog',
        highlight: 'map',
        title: 'Huh?',
        text: 'Looks like an unexpected Mutation - this Explorer has *a baseball bat!*\n\nYour Warriors can learn from the Explorer and will now be more effective against enemy Colonies.',
        image: 'popups/popups-ant-explorer-weapon.png',
    },
    createdBadAntExplorer: {
        type: 'dialog',
        highlight: 'map',
        text: 'Looks like an unexpected Mutation - this Explorer has a magic 8-ball and is *pretty much useless!*\n\n That happens - some Mutations will be good, some will be bad.',
        image: 'popups/popups-ant-explorer-useless.png',
    },
    foundParadise: {
        type: 'dialog',
        highlight: 'map',
        title: 'Wow!',
        text: 'This food source would really stabilize your Colony and it\'s a safe area without competition (and hopefully without Anteaters.)\n\n But how will you get there?\n\n*New goal: Move your Colony to a safe location.*',
    },

    // Chapter 6
    wontCreateAnotherAntExplorer: {
        type: 'dialog',
        highlight: 'antControlsExplorer',
        title: 'Create more Explorers',
        text: 'Remember: You can fight it out with the enemy, but that won\'t stop a major disaster.\n\nExplorers can find new territory and can produce unexpected results. *Build more Explorers* to expand.',
    },
    createdAttackBuffAntExplorer: {
        type: 'dialog',
        highlight: 'map',
        title: 'Improved Attack',
        text: 'A beneficial Mutation!\n\nYour new Explorer has taught your Warriors a *better way to attack*!',
        image: 'popups/popups-ant-explorer-weapon.png',
    },
    createdDefenseBuffAntExplorer: {
        type: 'dialog',
        highlight: 'map',
        title: 'Turtle Defense!',
        text: 'A beneficial Mutation!\n\nYour new Explorer has a *tougher exoskeleton* and all your ants have inherited the improvement!\n\nIt will be harder for the enemy to attack you now.',
        image: 'popups/popups-ant-explorer-defense.png',
    },
    createdSpeedBuffAntExplorer: {
        type: 'dialog',
        highlight: 'map',
        title: 'Rabbit Speed!',
        text: 'A beneficial Mutation!\n\nYour new Explorer is speedy and is training all your ants to *move faster*!\n\nThis will also allow your Workers to travel farther to find food.',
        image: 'popups/popups-ant-explorer-speed.png',
    },
    createdCarryBuffAntExplorer: {
        type: 'dialog',
        highlight: 'map',
        title: 'Heavy Lifter!',
        text: 'A beneficial Mutation!\n\nYour new Explorer has a new workout routine and is training all your Workers to *carry more food* at once!\n\nThis will allow your Workers to travel farther to find food.',
        image: 'popups/popups-ant-explorer-heavycarrier.png',
    },
    createdAnotherAttackBuffAntExplorer: {
        type: 'notification',
        text: 'Your new Explorer has another beneficial *attack* Mutation!',
    },
    createdAnotherDefenseBuffAntExplorer: {
        type: 'notification',
        text: 'Your new Explorer has another beneficial *defense* Mutation!',
    },
    createdAnotherSpeedBuffAntExplorer: {
        type: 'notification',
        text: 'Your new Explorer has another beneficial *speed* Mutation!',
    },
    createdAnotherCarryBuffAntExplorer: {
        type: 'notification',
        text: 'Your new Explorer has another beneficial *carrying capacity* Mutation!',
    },
    buildingParadiseBridge: {
        type: 'dialog',
        highlight: 'map',
        title: 'Gardening Tools!',
        text: 'A beneficial mutation.\n\nYour new Explorer has an idea of how to *build a bridge* to cross the river!',
        image: 'popups/popups-ant-explorer-gardening.png',
    },
    unlockParadise: {
        type: 'dialog',
        highlight: 'map',
        title: 'Nice!',
        text: 'The bridge is complete!\n\nTime to move the Colony to a new location.\n\nJust in time! It looks like the great laser beam of doom is angry and is destroying everything in sight!',
    },
    victory: {
        type: 'dialog',
        title: 'Victory!',
        text: 'You balanced your portfolio between *Workers* to harvest food, *Warriors* to expand and defend, and *Explorers* to avoid disaster by aggressively seeking out new, unexpected opportunities!\n\nSo how does this apply to the real world?',
        image: 'ant/queen/ant-queen.png',
        button: 'Let\'s go!',
        link: '/simulation',
    },

    // Simulation
    simulationIntro: {
        type: 'dialog',
        title: 'Real World Impact',
        text: 'In the real world, we often focus too much on the immediate dangers of competition and leave ourselves vulnerable to radical shifts in the environment.\n\nBut we can simulate how different portfolio mixes work with different levels of risk in the environment.',
        button: 'Let\'s go!',
    },
    simulationControls: {
        type: 'dialog',
        highlight: 'simulationControls',
        text: 'These are your Portfolio controls. (Just like with the ants.)\n\nLet\'s see what happens if we do *not* invest for the long run.\n\n(You can change it later.)',
    },
    simulationRisks: {
        type: 'dialog',
        highlight: 'simulationRisk',
        text: 'This is the level of risk in the environment.\n\nWhen your environment is stable, investing mostly in your core business is probably a safe bet.\n\nLet\'s see what happens when the risk is *LOW*.',
    },
    simulationCoreInfo: {
        label: 'help on core',
        type: 'dialog',
        highlight: 'simulationControlsCore',
        text: 'This is the % of your resources that you invest in the *Core Business*.\n\nThis is *Business as Usual*. Doing the things you know that deliver revenue and impact today.',
    },
    simulationAdjacentInfo: {
        label: 'help on adjacent',
        type: 'dialog',
        highlight: 'simulationControlsAdjacent',
        text: 'This is the % of your resources that you invest in *Adjacent Opportunities*.\n\nThese are activities that are pretty close to *Business as Usual*, but represent a new customer segment for your existing offering or new offering for your existing customers.',
    },
    simulationDisruptiveInfo: {
        label: 'help on disruptive',
        type: 'dialog',
        highlight: 'simulationControlsDisruptive',
        text: 'This is the % of your resources that you invest in *Transformational Projects*.\n\nThese are the real longshot projects that often don\'t seem to deliver any value... *BUT*\n\n...sometimes one of these bets turns out to be exactly the right thing to survive a catastrophic change in the playing field.',
    },
    simulationRisksInfo: {
        label: 'help on level of risks',
        type: 'dialog',
        highlight: 'simulationRisk',
        text: 'This is the level of risk in the environment, and *risks can change over time.*\n\nSome environments are riskier than others, but every environment has some risk.\n\nAn extremely risky environment is one where the rules are constantly changing and you have no choice but to react rapidly.',
    },
    simulationGraphInfo: {
        label: 'help on graph',
        type: 'dialog',
        highlight: 'simulationGraph',
        text: 'This graph shows the level of revenue (or impact) as time progresses. The higher on the graph, the more revenue or impact.\n\nYour business may start delivering value right away, but quickly run into issues depending on the level of risk in the environment.',
    },
};